import { useEffect, useState } from "react";
import { Archive, LogOut, Wallet } from "lucide-react";
import { healthz } from "../api/client";
import type { HealthResponse } from "../api/types";
import type { WalletSession } from "../hooks/useWalletSession";
import { shortWallet } from "../hooks/useWalletSession";

type Props = {
  session: WalletSession | null;
  connecting: boolean;
  vaultCount: number;
  onConnect: () => void;
  onDisconnect: () => void;
  onOpenVault: () => void;
};

export const TopNav = ({
  session,
  connecting,
  vaultCount,
  onConnect,
  onDisconnect,
  onOpenVault,
}: Props) => {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    healthz()
      .then((h) => alive && setHealth(h))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, []);

  const db = health?.database;
  const dotColor = error
    ? "#ef4444"
    : !health
      ? "rgba(255,255,255,0.3)"
      : db === "up"
        ? "#4ade80"
        : db === "not_configured"
          ? "#fbbf24"
          : "#ef4444";

  const statusText = error
    ? "offline"
    : !health
      ? "checking…"
      : db === "up"
        ? `db · ${health.paywrap}`
        : db === "not_configured"
          ? "db · not configured"
          : "db · down";

  return (
    <header className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between px-6 py-4 pointer-events-none">
      <div className="flex items-center gap-3 pointer-events-auto">
        <img
          src="/brand/clobsidian.png"
          alt=""
          className="w-8 h-8 rounded-lg object-cover shadow-[0_0_24px_-6px_rgba(168,85,247,0.75)]"
        />
        <div className="font-display font-extrabold text-white text-lg tracking-tight">
          clobsidian
        </div>
        <div className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 ml-2">
          provenance graph
        </div>
      </div>
      <div className="flex items-center gap-3 pointer-events-auto">
        {session ? (
          <div className="hidden sm:flex items-center gap-2 chip chip-muted">
            <Wallet className="h-3.5 w-3.5" />
            <span>{shortWallet(session.wallet)}</span>
          </div>
        ) : (
          <button
            onClick={onConnect}
            disabled={connecting}
            className="chip hover:opacity-80 disabled:opacity-50"
          >
            <Wallet className="h-3.5 w-3.5" />
            <span>{connecting ? "signing" : "browser"}</span>
          </button>
        )}
        <button onClick={onOpenVault} className="chip hover:opacity-80">
          <Archive className="h-3.5 w-3.5" />
          <span>vault {vaultCount}</span>
        </button>
        {session && (
          <button
            onClick={onDisconnect}
            className="chip chip-muted hover:opacity-80"
            aria-label="Disconnect wallet"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        )}
        <div className="flex items-center gap-2 chip chip-muted">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: dotColor, boxShadow: `0 0 8px ${dotColor}` }}
          />
          <span>{statusText}</span>
        </div>
        <a
          href="/openapi.json"
          target="_blank"
          rel="noreferrer"
          className="chip hover:opacity-80"
        >
          openapi
        </a>
      </div>
    </header>
  );
};
